import type { CancelToken } from 'axios';
import axios from 'axios';

import {
  ROVER_SEARCH_SOURCE_HEADER,
  ROVER_WARM_CACHE_HEADER,
} from '@rover/constants/src/platform/headers.constants';
import {
  ROVER_SEARCH_SOURCE_TYPE_CACHEABLE,
  ROVER_SEARCH_SOURCE_TYPE_SEO_LOCATION,
} from '@rover/constants/src/search/search.constants';
import { OVERNIGHT_BOARDING } from '@rover/constants/src/translations/services.constants';
import { convertToSearchApiKeys } from '@rover/react-lib/src/pages/search/SearchPage/utilities';
import { OptimizedSearchResponse } from '@rover/rsdk/src/apiClient/latest';
import appendUrlParams from '@rover/rsdk/src/modules/Network/appendUrlParams';
import { getAxiosInstance } from '@rover/rsdk/src/modules/Network/axios';
import httpContext from '@rover/rsdk/src/modules/Network/httpContext';
import type { Snakeify } from '@rover/rsdk/src/modules/Network/object-utilities';
import { camelize } from '@rover/rsdk/src/modules/Network/object-utilities';
import type { SearchFilters, SearchOptionsResponse } from '@rover/types';
import { fullApiUrl } from '@rover/utilities/apiUrl';

import DefaultAPIErrorHandler from './DefaultAPIErrorHandler';

export const SEARCH_URL: string = fullApiUrl('search-optimized/');

// Only send params that the API will use will help with caching
const filtersToRequestParams = (filters?: Partial<SearchFilters>) => {
  if (!filters || !Object.keys(filters).length) {
    return '';
  }

  const searchQuery = convertToSearchApiKeys({
    filters,
  });
  const params = Object.keys(searchQuery).map((key) => {
    const paramValue = searchQuery[key];

    if (Array.isArray(paramValue)) {
      return paramValue.map((value) => `${key}=${value}`).join('&');
    }

    return `${key}=${paramValue}`;
  });
  return `?${params.join('&')}`;
};

export const get = async (
  cancelToken: CancelToken | undefined,
  filters: Partial<SearchFilters> | undefined
): Promise<OptimizedSearchResponse | null> => {
  const ai = getAxiosInstance();
  const requestParams = filtersToRequestParams(filters);
  const urlToSearch = SEARCH_URL;

  // Send SEO page searches (which have an seoLocationSlug) as source type SEO_LOCATION
  const headers = {};
  if (filters?.seoLocationSlug) {
    headers[ROVER_SEARCH_SOURCE_HEADER] = ROVER_SEARCH_SOURCE_TYPE_SEO_LOCATION;
  }

  // Send Cache Warming searches as source type CACHEABLE
  if (process.env.JS_ENV_SERVER) {
    const req = httpContext.getReq();
    if (req?.headers[ROVER_WARM_CACHE_HEADER]) {
      headers[ROVER_SEARCH_SOURCE_HEADER] = ROVER_SEARCH_SOURCE_TYPE_CACHEABLE;
    }
  }

  try {
    const { data } = await ai.get<Snakeify<OptimizedSearchResponse>>(
      `${urlToSearch}${requestParams}`,
      {
        cancelToken,
        headers,
      }
    );
    const camelData = camelize(data, true);
    return camelData;
  } catch (e: any) {
    if (axios.isCancel(e)) {
      // Cancelled search is good behavior, don't throw an exception
      return null;
    }

    return DefaultAPIErrorHandler(e, 'There was an error while fetching search data.');
  }
};

export const options = async (
  serviceType: string = OVERNIGHT_BOARDING().type,
  allServices = false
): Promise<SearchOptionsResponse> => {
  const ai = getAxiosInstance();

  try {
    const params = {
      service_type: serviceType,
      all_services: allServices ? allServices.toString() : '',
    };
    const url = appendUrlParams(SEARCH_URL, params);
    const { data } = await ai.options<Snakeify<SearchOptionsResponse>>(url);
    const camelData = camelize(data, true);

    if (!camelData.actions) {
      throw new Error(`Could not get search options for service type: ${serviceType}`);
    }

    return camelData;
  } catch (e: any) {
    return DefaultAPIErrorHandler(e, 'There was an error while fetching search options.');
  }
};

export default { get };
